import React, { useState, useEffect } from "react";
import { 
  History, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  Clock, 
  Bell,
  X,
  ChevronRight,
  AlertTriangle,
  Building2,
  MessageSquareWarning
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { apiRequest, formatDate, formatDateTime, formatCurrency } from "../../lib/utils";
import { toast } from "sonner";

const getComplaintStatusColor = (status) => {
  switch (status) {
    case "open": return "bg-red-100 text-red-800";
    case "in_progress": return "bg-yellow-100 text-yellow-800";
    case "resolved": return "bg-green-100 text-green-800";
    case "closed": return "bg-gray-100 text-gray-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case "high": return "bg-red-100 text-red-700";
    case "medium": return "bg-orange-100 text-orange-700";
    case "low": return "bg-blue-100 text-blue-700";
    default: return "bg-gray-100 text-gray-700";
  }
};

export const CustomerHistoryModal = ({ open, onClose, customerId, customerName }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [view, setView] = useState("services");

  useEffect(() => {
    if (open && customerId) {
      fetchCustomerHistory();
    }
  }, [open, customerId]);

  const fetchCustomerHistory = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/customers/${customerId}/profile`);
      setProfile(data);
    } catch (error) {
      toast.error("Failed to load customer history");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const services = profile?.service_history || [];
  const complaints = profile?.complaint_history || [];
  const intelligence = profile?.service_intelligence || {};
  const reminder = profile?.reminder_status || {};
  const customer = profile?.customer || {};
  const branch = profile?.branch || {};

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl p-0">
        <SheetHeader className="sticky top-0 bg-white z-10 p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#ED1C24]/10">
                <History className="w-5 h-5 text-[#ED1C24]" />
              </div>
              <div>
                <SheetTitle className="text-left font-heading">
                  {customerName || "Customer"} History
                </SheetTitle>
                <div className="flex items-center gap-2 mt-0.5">
                  {branch?.name && (
                    <Badge variant="outline" className="text-xs">
                      <Building2 className="w-3 h-3 mr-1" />
                      {branch.name}
                    </Badge>
                  )}
                  {customer?.source_type && (
                    <Badge variant="secondary" className="text-xs">
                      {customer.source_type === "lead" ? "Converted Lead" : "SDR Customer"}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ED1C24]"></div>
          </div>
        ) : (
          <div className="overflow-y-auto h-[calc(85vh-80px)]">
            {/* Intelligence Summary */}
            <div className="p-4 bg-gray-50 border-b">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Service Intelligence
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Avg Interval</span>
                  </div>
                  <p className="font-bold font-heading">
                    {intelligence.average_interval_days || 0} days
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    <span className="text-xs text-muted-foreground">Last 3 Total</span>
                  </div>
                  <p className="font-bold font-heading">
                    {formatCurrency(intelligence.last_3_services_total || 0)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-muted-foreground">Avg Spend</span>
                  </div>
                  <p className="font-bold font-heading">
                    {formatCurrency(intelligence.average_service_spend || 0)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-muted-foreground">Next Service</span>
                  </div>
                  <p className="font-bold font-heading text-sm">
                    {intelligence.next_service_prediction 
                      ? formatDate(intelligence.next_service_prediction) 
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Reminder Status */}
            {reminder && (reminder.reminder_75_days || reminder.reminder_85_days || reminder.reminder_90_days) && (
              <div className="p-4 border-b">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Reminder Schedule
                </h3>
                <div className="space-y-2">
                  {reminder.reminder_75_days && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">SMS (75 days)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatDate(reminder.reminder_75_days)}</span>
                        {reminder.sms_sent && <Badge variant="outline" className="text-xs">Sent</Badge>}
                      </div>
                    </div>
                  )}
                  {reminder.reminder_85_days && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">WhatsApp (85 days)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatDate(reminder.reminder_85_days)}</span>
                        {reminder.whatsapp_sent && <Badge variant="outline" className="text-xs">Sent</Badge>}
                      </div>
                    </div>
                  )}
                  {reminder.reminder_90_days && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Call Task (90 days)</span>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{formatDate(reminder.reminder_90_days)}</span>
                        {reminder.call_task_created && <Badge variant="outline" className="text-xs">Created</Badge>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View Toggle */}
            <div className="p-4 border-b">
              <Tabs value={view} onValueChange={setView}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="services" data-testid="history-services-tab">
                    Services ({services.length})
                  </TabsTrigger>
                  <TabsTrigger value="complaints" data-testid="history-complaints-tab">
                    Complaints ({complaints.length})
                  </TabsTrigger>
                  <TabsTrigger value="calls" data-testid="history-calls-tab">
                    Calls ({profile?.call_history?.length || 0})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Content based on view */}
            <div className="p-4">
              {view === "services" && (
                <>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Service History ({services.length})
                  </h3>
                  {services.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No service history yet</p>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      <div className="space-y-4">
                        {services.map((service, index) => (
                          <div key={service.id || index} className="relative pl-10" data-testid={`service-${index}`}>
                            <div className="absolute left-2.5 w-3 h-3 rounded-full bg-[#ED1C24] border-2 border-white shadow-sm"></div>
                            <div className="bg-white rounded-lg border p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm">
                                  {service.service_type || "Service"}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(service.service_date)}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Amount</span>
                                <span className="font-semibold text-green-600">
                                  {formatCurrency(service.amount || 0)}
                                </span>
                              </div>
                              {service.branch_name && (
                                <div className="flex items-center justify-between text-sm mt-1">
                                  <span className="text-muted-foreground">Branch</span>
                                  <span>{service.branch_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {view === "complaints" && (
                <>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
                    <MessageSquareWarning className="w-4 h-4" />
                    Complaint History ({complaints.length})
                  </h3>
                  {complaints.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <MessageSquareWarning className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No complaints recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {complaints.map((complaint, index) => (
                        <div key={complaint.id || index} className="bg-white rounded-lg border p-3" data-testid={`complaint-${index}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{complaint.subject}</p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {complaint.description}
                              </p>
                            </div>
                            <Badge className={`text-xs ml-2 ${getComplaintStatusColor(complaint.status)}`}>
                              {complaint.status?.replace("_", " ")}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mt-2 pt-2 border-t">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`${getPriorityColor(complaint.priority)}`}>
                                {complaint.priority}
                              </Badge>
                              <span>{formatDate(complaint.created_at)}</span>
                            </div>
                            {complaint.resolved_at && (
                              <span className="text-green-600">Resolved: {formatDate(complaint.resolved_at)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {view === "calls" && (
                <>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Call History ({profile?.call_history?.length || 0})
                  </h3>
                  {!profile?.call_history || profile.call_history.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No call history yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {profile.call_history.map((call, index) => (
                        <div key={call.id || index} className="bg-white rounded-lg border p-3" data-testid={`call-${index}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant={call.outcome === "answered" ? "default" : "secondary"} className="text-xs">
                              {call.outcome?.replace("_", " ")}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatDateTime(call.created_at)}
                            </span>
                          </div>
                          {call.remark && (
                            <p className="text-sm text-muted-foreground">{call.remark}</p>
                          )}
                          {call.follow_up_date && (
                            <p className="text-xs text-blue-600 mt-1">
                              Follow-up: {formatDate(call.follow_up_date)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Compact History Button Component for use in customer rows
export const HistoryButton = ({ customerId, customerName, variant = "icon", size = "icon" }) => {
  const [open, setOpen] = useState(false);

  // For mobile card view, use larger touch-friendly button
  if (size === "default") {
    return (
      <>
        <Button
          variant="outline"
          className="h-11 w-11 text-gray-600 hover:text-[#ED1C24] hover:border-[#ED1C24]"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          data-testid={`history-btn-${customerId}`}
        >
          <History className="w-5 h-5" />
        </Button>
        <CustomerHistoryModal
          open={open}
          onClose={() => setOpen(false)}
          customerId={customerId}
          customerName={customerName}
        />
      </>
    );
  }

  if (variant === "icon") {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          className="w-8 h-8 text-gray-500 hover:text-[#ED1C24] hover:bg-[#ED1C24]/10"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
          data-testid={`history-btn-${customerId}`}
        >
          <History className="w-4 h-4" />
        </Button>
        <CustomerHistoryModal
          open={open}
          onClose={() => setOpen(false)}
          customerId={customerId}
          customerName={customerName}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-gray-600 hover:text-[#ED1C24] hover:border-[#ED1C24]"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid={`history-btn-${customerId}`}
      >
        <History className="w-4 h-4 mr-1" />
        History
      </Button>
      <CustomerHistoryModal
        open={open}
        onClose={() => setOpen(false)}
        customerId={customerId}
        customerName={customerName}
      />
    </>
  );
};

export default CustomerHistoryModal;
